/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { routes } from '@/route-paths';
import { cn } from '@/lib/utils';

export interface ProductListingNoResultsProps {
    /**
     * The search term that produced no results. When present the message names
     * the query back to the shopper; the PLP variant omits it.
     */
    query?: string;
    className?: string;
}

/**
 * Empty state for the PLP and search results surfaces.
 *
 * Rendered in place of the product grid when a category or query returns no
 * matching products. `role="status"` so assistive tech announces the outcome
 * after a filter or query change without moving focus.
 */
export default function ProductListingNoResults({ query, className }: ProductListingNoResultsProps): ReactElement {
    const { t } = useTranslation('category');

    return (
        <div
            data-testid="product-listing-no-results"
            role="status"
            className={cn('flex flex-col items-center gap-4 py-16 text-center', className)}>
            <h2 className="type-subheading uppercase text-foreground">
                {query
                    ? t('noResults.headingWithQuery', {
                          query,
                          defaultValue: 'No results for "{{query}}"',
                      })
                    : t('noResults.heading', { defaultValue: 'No results found' })}
            </h2>
            <p className="type-body max-w-prose text-muted-foreground">
                {t('noResults.body', {
                    defaultValue: 'Try removing a filter, checking your spelling, or searching for something else.',
                })}
            </p>
            <Link to={routes.home} className="type-label-medium uppercase underline hover:no-underline">
                {t('noResults.continueShopping', { defaultValue: 'Continue shopping' })}
            </Link>
        </div>
    );
}
